// Hook customizado para gerenciar estado do formulário de login
import { useState, useCallback, useMemo } from "react";
import { storageService } from "../services/storageService";

/**
 * Hook para gerenciar o formulário de login
 * @returns {Object} Estado e funções do formulário
 */
export const useLoginForm = () => {
  // Estado do formulário
  const [formData, setFormData] = useState({
    user: "",
    password: ""
  });

  // Estado de erros de validação
  const [errors, setErrors] = useState({});

  // Estado de visibilidade da senha
  const [showPassword, setShowPassword] = useState(false);

  /**
   * Atualiza um campo do formulário
   * @param {string} field - Nome do campo (user, password)
   * @param {string} value - Novo valor do campo
   */
  const updateField = useCallback((field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      
      // Salva no localStorage quando user ou password mudam
      if (field === "user" || field === "password") {
        storageService.setUser(updated.user, updated.password);
      }
      
      return updated;
    });

    // Limpa erro do campo quando o usuário começa a digitar
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  }, [errors]);

  /**
   * Alterna a visibilidade da senha
   */
  const togglePasswordVisibility = useCallback(() => {
    setShowPassword(prev => !prev);
  }, []);

  /**
   * Define erros de validação
   * @param {Object} newErrors - Objeto com erros por campo
   */
  const setFormErrors = useCallback((newErrors) => {
    setErrors(newErrors);
  }, []);

  /**
   * Reseta o formulário
   */
  const resetForm = useCallback(() => {
    setFormData({
      user: "",
      password: ""
    });
    setErrors({});
    setShowPassword(false);
  }, []);

  // Objeto JSON com credenciais (memoizado)
  const userObject = useMemo(() => {
    return JSON.stringify({ user: formData.user, password: formData.password });
  }, [formData.user, formData.password]);

  return {
    formData,
    errors,
    showPassword,
    userObject,
    updateField,
    setErrors: setFormErrors,
    togglePasswordVisibility,
    resetForm
  };
};

